import { useEffect, useState } from 'react';

import { useInView } from './useInView';

const DURATION_MS = 1400;

/** Fast at first, settling at the end — a number that lands rather than stops. */
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * Counts up to `value` the first time the number scrolls into view.
 *
 * The animation runs on one requestAnimationFrame loop that cancels itself at
 * the end, so no timer survives the count. Reduced-motion visitors, and anyone
 * without JavaScript, get the final figure immediately: `useInView` reports
 * `static`, and `static` renders `value`.
 */
export function useCountUp(value: number) {
  const { ref, state } = useInView<HTMLSpanElement>({ skipAboveFold: false });
  const [shown, setShown] = useState(value);

  useEffect(() => {
    if (state === 'static') return setShown(value);
    if (state === 'waiting') return setShown(0);

    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / DURATION_MS);
      setShown(Math.round(easeOut(progress) * value));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [state, value]);

  return { ref, shown };
}
